import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Sun, Moon, Check } from 'lucide-react';
import { spacing, fontSize, fontWeight, borderRadius, shadows } from '../../styles/theme';
import BackButton from '../components/BackButton';
import { useTheme } from '../../contexts/ThemeContext';

const Container = styled.div`
  height: 100vh;
  background-color: ${props => props.theme.colors.background};
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: ${spacing.md};
  background-color: ${props => props.theme.colors.surface};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  
  @media (max-width: 768px) {
    padding: ${spacing.sm};
  }
`;


const HeaderTitle = styled.h1`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  flex: 1;
  text-align: center;
  margin-right: 32px; /* Compensate for back button */
`;

const Content = styled.div`
  flex: 1;
  background-color: ${props => props.theme.colors.background};
  height: calc(100vh - 52px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
  padding: ${spacing.lg};
`;

const SettingsSection = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.xl};
  margin-bottom: ${spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin: 0 0 ${spacing.lg} 0;
`;

const SectionDescription = styled.p`
  font-size: ${fontSize.md};
  color: ${props => props.theme.colors.muted};
  line-height: 1.6;
  margin: 0 0 ${spacing.lg} 0;
`;

const ThemeOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${spacing.md};
  margin-bottom: ${spacing.lg};
`;

const ThemeOption = styled.button`
  padding: ${spacing.lg};
  border: 2px solid ${props => props.selected ? props.theme.colors.brand : props.theme.colors.border};
  border-radius: ${borderRadius.lg};
  background: ${props => props.selected 
    ? `${props.theme.colors.brand}20` 
    : props.theme.colors.surface};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  text-align: left;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.sm};
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &:hover {
    border-color: ${props => props.disabled ? props.theme.colors.border : props.theme.colors.brand}50;
    background: ${props => props.disabled ? props.theme.colors.surface : props.theme.colors.border};
    transform: ${props => props.disabled ? 'none' : 'translateY(-2px)'};
  }
`;

const ThemeIcon = styled.div`
  font-size: 32px;
  color: ${props => props.selected ? props.theme.colors.brand : props.theme.colors.muted};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ThemeName = styled.div`
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  text-align: center;
`;

const ThemeSubtitle = styled.div`
  font-size: ${fontSize.sm};
  color: ${props => props.theme.colors.muted};
  text-align: center;
  line-height: 1.3;
`;

const SaveButton = styled.button`
  background: linear-gradient(135deg, ${props => props.theme.colors.brand}, ${props => props.theme.colors.warning});
  color: white;
  border: none;
  border-radius: ${borderRadius.md};
  padding: ${spacing.md} ${spacing.xl};
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  transition: all 0.2s ease;
  margin-top: ${spacing.lg};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(234, 115, 23, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const StatusMessage = styled.div`
  padding: ${spacing.md};
  border-radius: ${borderRadius.md};
  margin-top: ${spacing.md};
  font-size: ${fontSize.sm};
  display: flex;
  align-items: center;
  gap: ${spacing.sm};

  ${props => props.type === 'success' && `
    background: rgba(34, 197, 94, 0.1);
    color: #22c55e;
    border: 1px solid rgba(34, 197, 94, 0.2);
  `}

  ${props => props.type === 'error' && `
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.2);
  `}

  ${props => props.type === 'info' && `
    background: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
    border: 1px solid rgba(59, 130, 246, 0.2);
  `}
`;

const SelectedIndicator = styled.div`
  position: absolute;
  top: ${spacing.sm};
  right: ${spacing.sm};
  background: ${props => props.theme.colors.brand};
  border-radius: 50%;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const THEMES = [
  {
    id: 'dark',
    name: 'Dark',
    subtitle: 'Donker thema',
    icon: Moon,
    description: 'Klassiek donker thema met lichte tekst op donkere achtergrond.'
  },
  {
    id: 'light',
    name: 'Light',
    subtitle: 'Coming Soon',
    icon: Sun,
    description: 'Helder licht thema met donkere tekst op lichte achtergrond.',
    disabled: true
  }
];

export default function LayoutSettingsScreen() {
  const { themeMode, setTheme, colors } = useTheme();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const handleBack = () => {
    window.history.back();
  };

  const handleThemeChange = (themeId) => {
    const theme = THEMES.find(t => t.id === themeId);
    if (theme?.disabled) return;
    
    setTheme(themeId);
    setStatus({
      type: 'success',
      message: 'Thema succesvol gewijzigd!'
    });
    setTimeout(() => setStatus(null), 3000);
  };

  const handleSave = async () => {
    setLoading(true);
    setStatus(null);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setStatus({
        type: 'success',
        message: 'Layout instellingen succesvol opgeslagen!'
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Er is een fout opgetreden bij het opslaan van de instellingen.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>

      <Content>
        <SettingsSection>
          <SectionTitle>Thema Instellingen</SectionTitle>
          <SectionDescription>
            Kies uw voorkeursthema voor de KMFC Shop administratie.
          </SectionDescription>

          <ThemeOptions>
            {THEMES.map((theme) => (
              <ThemeOption
                key={theme.id}
                selected={themeMode === theme.id}
                disabled={theme.disabled}
                onClick={() => handleThemeChange(theme.id)}
              >
                <ThemeIcon as={theme.icon} />
                <ThemeName>{theme.name}</ThemeName>
                <ThemeSubtitle>{theme.subtitle}</ThemeSubtitle>
                {themeMode === theme.id && (
                  <SelectedIndicator>
                    <Check size={16} color="white" />
                  </SelectedIndicator>
                )}
              </ThemeOption>
            ))}
          </ThemeOptions>

          {status && (
            <StatusMessage type={status.type}>
              {status.message}
            </StatusMessage>
          )}

          <SaveButton onClick={handleSave} disabled={loading}>
            {loading ? (
              <>
                <div style={{ 
                  width: '18px', 
                  height: '18px', 
                  border: '2px solid white', 
                  borderTop: '2px solid transparent', 
                  borderRadius: '50%', 
                  animation: 'spin 1s linear infinite' 
                }}></div>
                Opslaan...
              </>
            ) : (
              <>
                <Check size={18} />
                Instellingen Opslaan
              </>
            )}
          </SaveButton>
        </SettingsSection>
      </Content>
    </Container>
  );
}
